/**
 * AuthService — the single source of truth for authentication state.
 *
 * MuseFlow uses two identity stacks because Amplify v6 cannot run OAuth flows
 * inside a Capacitor WebView:
 *   - aws-amplify/auth ........ username/password (Cognito user pool)
 *   - oidc-client-ts .......... Google / Apple OAuth (Cognito Hosted UI)
 *
 * This module unifies them behind one state machine. The durable truth is the
 * token storage of each stack (localStorage on web, Capacitor Preferences on
 * native); everything else — isAuthenticated, the user profile, tokens — is
 * derived on demand and never persisted separately.
 *
 * Invariants (see documentation/auth-architecture.md):
 *   1. Access tokens are never stored in React/Redux state. Consumers call
 *      getToken() every time; the only caching is the single-flight refresh.
 *   2. resolveSession() performs LOCAL reads only. It never blocks on the
 *      network, so it is safe to gate routing on it.
 *   3. A definitive "no session" resolves to 'unauthenticated'. There is no
 *      optimistic fallback to persisted flags (the MUS-264 zombie state).
 */
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser as amplifyGetCurrentUser,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import { isCapacitorNative } from './Constants';
import { CapacitorCache } from './CapacitorStorage';
import logger from './Logger';
import { awsRum } from './AwsRum';
import { reportError } from './reportError';

export type AuthStatus = 'resolving' | 'authenticated' | 'unauthenticated';

export interface AuthProfile {
  email: string | undefined;
  sub: string | undefined;
  phone_number: string | undefined;
  name: string | undefined;
}

export interface AuthSnapshot {
  status: AuthStatus;
  profile: AuthProfile | undefined;
}

export enum ScopesEnum {
  'UNITS_READ' = 'museflow:units:read',
  'UNITS_WRITE' = 'museflow:units:write',
  'TUTORIALS_READ' = 'museflow:tutorials:read',
  'TUTORIALS_WRITE' = 'museflow:tutorials:write',
  'LEVELS_READ' = 'museflow:levels:read',
  'LEVELS_WRITE' = 'museflow:levels:write',
  'PHRASES_WRITE' = 'museflow:phrases:write',
  'PHRASES_READ' = 'museflow:phrases:read',
  'REPERTOIRES_READ' = 'museflow:repertoires:read',
  'REPERTOIRES_WRITE' = 'museflow:repertoires:write',
  'USER_LEVEL_PROGRESS_READ' = 'museflow:user_level_progress:read',
  'USER_LEVEL_PROGRESS_WRITE' = 'museflow:user_level_progress:write',
  'USERDATA_READ' = 'museflow:userdata:read',
  'USERDATA_WRITE' = 'museflow:userdata:write',
  'USERDATA_ADMIN_READ' = 'museflow:userdata:admin:read',
  'USERDATA_ADMIN_WRITE' = 'museflow:userdata:admin:write',
  'USER_REPERTOIRE_PROGRESS_WRITE' = 'museflow:user_repertoire_progress:write',
  'USER_EXERCISE_PROGRESS_READ' = 'museflow:user_exercise_progress:read',
  'USER_EXERCISE_PROGRESS_WRITE' = 'museflow:user_exercise_progress:write',
  'EVENTS_WRITE' = 'museflow:events:write',
  'EVENTS_READ' = 'museflow:events:read',
  'EVENTS_ADMIN_WRITE' = 'museflow:events:admin:write',
  'EVENTS_ADMIN_READ' = 'museflow:events:admin:read',
  'NOTIFICATIONS_READ' = 'museflow:notifications:read',
  'NOTIFICATIONS_WRITE' = 'museflow:notifications:write',
  'PLAY_SESSION_WRITE' = 'museflow:playsession:write',
  'PLAY_SESSION_READ' = 'museflow:playsession:read',
  'PLAY_SESSION_ADMIN_WRITE' = 'museflow:playsession:admin:write',
  'PLAY_SESSION_ADMIN_READ' = 'museflow:playsession:admin:read',
  'STRIPE_ADMIN_WRITE' = 'museflow:stripe:admin:write',
  'STRIPE_ADMIN_READ' = 'museflow:stripe:admin:read',
  'METRICS_READ' = 'museflow:metrics:read',
}

export const userScopes = [
  ScopesEnum.USER_LEVEL_PROGRESS_WRITE,
  ScopesEnum.USER_LEVEL_PROGRESS_READ,
  ScopesEnum.PHRASES_READ,
  ScopesEnum.LEVELS_READ,
  ScopesEnum.TUTORIALS_READ,
  ScopesEnum.UNITS_READ,
  ScopesEnum.USERDATA_WRITE,
  ScopesEnum.USER_REPERTOIRE_PROGRESS_WRITE,
  ScopesEnum.USER_EXERCISE_PROGRESS_READ,
  ScopesEnum.USER_EXERCISE_PROGRESS_WRITE,
  ScopesEnum.REPERTOIRES_READ,
  ScopesEnum.REPERTOIRES_WRITE,
  ScopesEnum.EVENTS_WRITE,
  ScopesEnum.NOTIFICATIONS_READ,
  ScopesEnum.NOTIFICATIONS_WRITE,
  ScopesEnum.PLAY_SESSION_WRITE,
  ScopesEnum.METRICS_READ,
];

export const oidcCache = new CapacitorCache();

export const oidcManager = new UserManager({
  authority: `https://cognito-idp.us-west-2.amazonaws.com/${process.env.REACT_APP_COGNITO_USER_POOL_ID}`,
  client_id: process.env.REACT_APP_COGNITO_CLIENT_ID!,
  redirect_uri: isCapacitorNative() ? 'museflow://auth-callback' : `${window.location.origin}/`,
  response_type: 'code',
  scope:
    'openid email profile ' +
    userScopes.map((scope) => `${process.env.REACT_APP_OAUTH_REDIRECT_URL}/${scope}`).join(' '),
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: oidcCache }),
});

const TOKEN_EXPIRY_SKEW_MS = 30000;

/**
 * A missing or malformed token is treated as expired: callers asking about a
 * token they don't have need the refresh path, not a green light.
 */
export function tokenNeedsRefresh(jwt: string | undefined): boolean {
  if (!jwt) return true;
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return true;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = JSON.parse(atob(padded)) as { exp?: unknown };
    const expiresAtSeconds = typeof decoded.exp === 'number' ? decoded.exp : 0;
    return expiresAtSeconds * 1000 <= Date.now() + TOKEN_EXPIRY_SKEW_MS;
  } catch {
    return true;
  }
}

export function isInvalidGrantOrUnauthorized(error: unknown): boolean {
  const err = error as {
    message?: string;
    error?: string;
    status?: number;
    response?: { status?: number };
  };
  const message = err?.message ?? err?.error ?? String(error);
  const status = err?.status ?? err?.response?.status;
  return (
    status === 401 ||
    message.includes('401') ||
    message.includes('invalid_grant') ||
    err?.error === 'invalid_grant'
  );
}

function isDefinitivelyUnauthenticated(error: unknown): boolean {
  const name = (error as { name?: string })?.name;
  return name === 'UserUnAuthenticatedException' || name === 'UserNotConfirmedException';
}

// Amplify can briefly report "Auth UserPool not configured." while a native
// relaunch races Amplify.configure(). Retry within a bounded window so a cold
// start does not misread a valid session as missing (MUS-248).
const AMPLIFY_CONFIG_RETRY_ATTEMPTS = isCapacitorNative() ? 10 : 3;
const AMPLIFY_CONFIG_RETRY_DELAY_MS = 200;

async function getAmplifyUserWithConfigRetry(): Promise<{ username: string } | undefined> {
  for (let attempt = 1; attempt <= AMPLIFY_CONFIG_RETRY_ATTEMPTS; attempt++) {
    try {
      return await amplifyGetCurrentUser();
    } catch (error) {
      const isConfigRace =
        error instanceof Error && error.message === 'Auth UserPool not configured.';
      if (!isConfigRace || attempt === AMPLIFY_CONFIG_RETRY_ATTEMPTS) {
        throw error;
      }
      logger.warn(
        `[AuthService] Amplify not configured yet (attempt ${attempt}/${AMPLIFY_CONFIG_RETRY_ATTEMPTS}), retrying`,
      );
      await new Promise((resolve) => setTimeout(resolve, AMPLIFY_CONFIG_RETRY_DELAY_MS));
    }
  }
  return undefined;
}

type SessionInvalidListener = () => void;
type SignOutListener = () => void;

class AuthService {
  private snapshot: AuthSnapshot = { status: 'resolving', profile: undefined };
  private listeners = new Set<() => void>();
  private sessionInvalidListeners = new Set<SessionInvalidListener>();
  private signOutListeners = new Set<SignOutListener>();
  private resolveInFlight: Promise<AuthSnapshot> | null = null;
  private oidcRefreshInFlight: Promise<string | undefined> | null = null;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AuthSnapshot => this.snapshot;

  /**
   * Fired when an existing session is discovered to be irreparably dead
   * (refresh rejected with invalid_grant/401). The React layer subscribes to
   * purge app state. Not fired for ordinary sign-outs or fresh visitors.
   */
  onSessionInvalid = (listener: SessionInvalidListener): (() => void) => {
    this.sessionInvalidListeners.add(listener);
    return () => this.sessionInvalidListeners.delete(listener);
  };

  /**
   * Fired at the start of every sign-out: the user's own, and the one a dead
   * session forces. Lets the telemetry senders drop what they still hold for the
   * account, before another can sign in without a page reload.
   */
  onSignOut = (listener: SignOutListener): (() => void) => {
    this.signOutListeners.add(listener);
    return () => this.signOutListeners.delete(listener);
  };

  private setSnapshot(next: AuthSnapshot): void {
    const unchanged =
      next.status === this.snapshot.status &&
      next.profile?.sub === this.snapshot.profile?.sub &&
      next.profile?.email === this.snapshot.profile?.email &&
      next.profile?.name === this.snapshot.profile?.name &&
      next.profile?.phone_number === this.snapshot.profile?.phone_number;
    if (unchanged) return;
    this.snapshot = next;
    this.listeners.forEach((listener) => listener());
  }

  /**
   * Determine the session state from LOCAL token storage only — no network.
   * Single-flight: concurrent callers share one lookup. The result is
   * definitive: no stored session on either stack means 'unauthenticated'.
   */
  resolveSession = (): Promise<AuthSnapshot> => {
    if (!this.resolveInFlight) {
      this.resolveInFlight = this.resolveSessionInternal().finally(() => {
        this.resolveInFlight = null;
      });
    }
    return this.resolveInFlight;
  };

  private async resolveSessionInternal(): Promise<AuthSnapshot> {
    try {
      const oidcUser = await oidcManager.getUser();
      if (oidcUser) {
        // An expired access token is fine here: getToken() refreshes lazily,
        // and a dead refresh token surfaces through the invalid-grant path.
        this.setSnapshot({
          status: 'authenticated',
          profile: {
            email: oidcUser.profile.email,
            sub: oidcUser.profile.sub,
            phone_number: oidcUser.profile.phone_number as string | undefined,
            name: oidcUser.profile.name,
          },
        });
        return this.snapshot;
      }

      const amplifyUser = await getAmplifyUserWithConfigRetry();
      if (amplifyUser) {
        const attributes = await fetchUserAttributes();
        this.setSnapshot({
          status: 'authenticated',
          profile: {
            email: attributes.email,
            sub: attributes.sub,
            phone_number: attributes.phone_number,
            name: attributes.name,
          },
        });
        return this.snapshot;
      }
    } catch (error) {
      if (!isDefinitivelyUnauthenticated(error)) {
        logger.error('[AuthService] Unexpected error resolving session', error);
        awsRum?.recordError(error);
      }
    }
    this.setSnapshot({ status: 'unauthenticated', profile: undefined });
    return this.snapshot;
  }

  /**
   * Fetch a fresh access token. Never cached beyond the single-flight refresh:
   * token storage is the cache, and it knows about expiry — state does not.
   */
  getToken = async (): Promise<string | undefined> => {
    try {
      const oidcUser = await oidcManager.getUser();
      if (oidcUser) {
        if (!tokenNeedsRefresh(oidcUser.access_token)) {
          return oidcUser.access_token;
        }
        return await this.refreshOidcToken();
      }

      // Amplify refreshes internally when the cached session is expired.
      const session = await fetchAuthSession();
      const amplifyToken = session.tokens?.accessToken?.toString();
      if (amplifyToken) {
        return amplifyToken;
      }
    } catch (error) {
      if (isInvalidGrantOrUnauthorized(error)) {
        logger.error('[AuthService] Token refresh rejected — session is dead', error);
        await this.handleSessionInvalid();
        return undefined;
      }
      logger.warn('[AuthService] getToken failed', error);
      reportError(error, 'auth.getToken');
      return undefined;
    }

    if (this.snapshot.status === 'authenticated') {
      // We believed we had a session but neither stack can produce a token.
      logger.error('[AuthService] Authenticated state with no token available — reconciling');
      await this.handleSessionInvalid();
    }
    return undefined;
  };

  private refreshOidcToken(): Promise<string | undefined> {
    if (!this.oidcRefreshInFlight) {
      this.oidcRefreshInFlight = oidcManager
        .signinSilent()
        .then((user) => user?.access_token)
        .catch(async (error: unknown) => {
          if (isInvalidGrantOrUnauthorized(error)) {
            logger.error('[AuthService] OIDC silent refresh rejected — session is dead', error);
            await this.handleSessionInvalid();
            return undefined;
          }
          reportError(error, 'auth.refresh');
          throw error;
        })
        .finally(() => {
          this.oidcRefreshInFlight = null;
        });
    }
    return this.oidcRefreshInFlight;
  }

  /** Re-read the session after a sign-in, OAuth callback, or profile update. */
  refreshSession = (): Promise<AuthSnapshot> => this.resolveSession();

  /**
   * Boot entry point. resolveSession() only does local reads, so the watchdog
   * firing means storage itself is wedged — that is a bug to surface, not an
   * expected path. We fail OPEN to 'unauthenticated' (worst case: the user
   * signs in again). Failing toward 'authenticated' on missing evidence is
   * what produced the MUS-264 deadlock.
   */
  bootstrapSession = (watchdogMs: number): Promise<AuthSnapshot> => {
    let watchdogId: ReturnType<typeof setTimeout> | undefined;
    const watchdog = new Promise<AuthSnapshot>((resolve) => {
      watchdogId = setTimeout(() => {
        const error = new Error(
          `[AuthService] Session resolution still pending after ${watchdogMs}ms — local storage may be wedged. Continuing unauthenticated.`,
        );
        logger.error(error.message);
        awsRum?.recordError(error);
        this.setSnapshot({ status: 'unauthenticated', profile: undefined });
        resolve(this.snapshot);
      }, watchdogMs);
    });
    const resolution = this.resolveSession().finally(() => {
      if (watchdogId) clearTimeout(watchdogId);
    });
    return Promise.race([resolution, watchdog]);
  };

  /**
   * Tear down both identity stacks. App-level state cleanup (redux purge,
   * storage clear) belongs to the React layer, which wraps this call.
   */
  signOut = async (): Promise<void> => {
    this.signOutListeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        logger.warn('[AuthService] Sign-out listener failed', error);
      }
    });
    this.setSnapshot({ status: 'unauthenticated', profile: undefined });
    try {
      await oidcManager.removeUser();
    } catch (error) {
      logger.warn('[AuthService] Failed to remove OIDC user during sign-out', error);
    }
    try {
      await amplifySignOut();
    } catch (error) {
      logger.warn('[AuthService] Amplify sign-out failed', error);
    }
  };

  private async handleSessionInvalid(): Promise<void> {
    const hadSession = this.snapshot.status === 'authenticated';
    await this.signOut();
    if (hadSession) {
      this.sessionInvalidListeners.forEach((listener) => listener());
    }
  }

  /**
   * For non-React callers (axios interceptor) that learn the session is dead
   * from an API response (e.g. 401 with invalid_grant).
   */
  reportSessionInvalid = (): Promise<void> => this.handleSessionInvalid();
}

export const authService = new AuthService();
